import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { routeMatches } from "@/Routing/routeMatches";

/*
 * Route-level cross-fade. Lives in App/ rather than Shared/Layout/ because it's
 * router infrastructure, not a shared component: its only callers are AppRouter,
 * the two feature routers and NotAuthedRoute, and it renders no UI of its own —
 * it matches a path, then fades the matching screen in and the outgoing one out.
 * (Sibling precedent: AppFadeInShell, which does the same job at boot.)
 */

/* Grid items default to min-width:auto, which forbids shrinking below the
 * content's min-content width — so a screen with wide content (e.g. the quote
 * rows) held the whole page at that width and overflowed the window instead of
 * letting the content's own responsive modes kick in.
 *
 * The enter/exit state is a class, never the HTML `hidden` attribute: every CSS
 * reset we ship layers defines [hidden] { display: none !important }, and
 * mid-mount that gave descendants like the ShipmentMap Mapbox canvas a 0x0
 * bounding rect, which it then latched onto. */
const pageBodyClasses =
  "[grid-area:1/1] min-w-0 transition-[opacity,transform] duration-300 delay-300 ease-[cubic-bezier(0.5,0,0.5,1)]";
const pageBodyHiddenClasses =
  "[grid-area:1/1] min-w-0 transition-[opacity,transform] duration-300 delay-0 ease-[cubic-bezier(0.5,0,0.5,1)] opacity-0 scale-95";

/** Stacks every Page in one grid cell so the outgoing and incoming screens
 *  overlap during the cross-fade instead of pushing each other around.
 *
 *  The stacking comes from the grid-area on the children — the container needs
 *  no template of its own, and must not be given one as an arbitrary value. It
 *  used to set a template whose track sizes were the bare number 1. That is not
 *  a valid track size, so browsers dropped the declaration and the dev server
 *  looked right; the production CSS minifier normalises the bare number to 1px
 *  rather than dropping it, which laid every route out inside a 1px-wide cell.
 *  It only ever went wrong in the built bundle, so no local run, snapshot or
 *  Storybook build could see it. (Don't write the old class name in this file
 *  either — Tailwind scans comments too, and would generate the rule again.) */
export function PageContainer({ children }: { children: ReactNode }) {
  return <div className="grid">{children}</div>;
}

interface PageProps {
  path: string;
  children: () => ReactNode;
  exact?: boolean | undefined;
  id?: string | undefined;
}

// Inspiration for this technique came from : https://dev.to/joostkiens/til-a-better-way-to-handle-in-out-transitions-2bc1
export function Page(props: PageProps) {
  /**
   * Lifecycle of a page
   * - Born hidden, when first matches
   * - 50ms later, hidden is removed
   *
   * - When it no longer matches, remove the hidden tag
   * - 300ms later, we stop rendering it
   */

  const location = useLocation();

  const match = routeMatches(location.pathname, props.path, props.exact);

  const [render, setRender] = useState(false);
  const [hidden, setHidden] = useState(true);

  const [clearRenderTimeout, setClearRenderTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [clearHiddenTimeout, setClearHiddenTimeout] = useState<
    NodeJS.Timeout | undefined
  >();

  // console.log(`Rendering page`, {
  //     path: props.path,
  //     matchInfo,
  //     match,
  //     render,
  //     hidden
  // });

  useEffect(
    function () {
      //console.log(`useEffect match=${match}`);
      if (!match) {
        if (clearHiddenTimeout !== undefined) {
          clearTimeout(clearHiddenTimeout);
        }

        setHidden(true);
        //console.log(` - ${props.path} : setting render timeout`);
        setClearRenderTimeout(
          setTimeout(function () {
            // console.log(
            //     ` - ${props.path} : setting render to false`
            // );
            setRender(false);
          }, 300),
        );
      } else {
        if (clearRenderTimeout !== undefined) {
          //console.log(` - ${props.path} : clearing render timeout`);
          clearTimeout(clearRenderTimeout);
        }
        setRender(true);
        setClearHiddenTimeout(
          setTimeout(function () {
            setHidden(false);
          }, 50),
        );
      }

      return function cleanup() {
        if (clearHiddenTimeout !== undefined) {
          // console.log(
          //     ` - ${props.path} : clean up - clearing hidden timeout`
          // );
          clearTimeout(clearHiddenTimeout);
        }

        if (clearRenderTimeout !== undefined) {
          // console.log(
          //     ` - ${props.path} : clean up - clearing render timeout`
          // );
          clearTimeout(clearRenderTimeout);
        }
      };
    },

    [match],
  );

  if (!render) {
    return <></>;
  }
  return (
    <div
      id={props.id}
      className={hidden ? pageBodyHiddenClasses : pageBodyClasses}
    >
      {props.children()}
    </div>
  );
}
